/**
 * The design handoff's worked example, in one place.
 *
 * EVERY VALUE HERE IS ILLUSTRATIVE. The handoff's own words: "internally
 * consistent — figures reconcile across the financial check, the risk review
 * and the fee summary. Treat it as fixture data, not as legally verified
 * content." Nothing in this file has been checked against the Immigration
 * Rules, the figures register, or anything else. It exists so the screens can
 * be reviewed with realistic content in them while their real data sources are
 * built.
 *
 * It is deliberately one module, named `sample`, imported only by screens that
 * are not yet wired, and always rendered under `previewBanner()`. When a screen
 * gets its real source, its slice here should be deleted rather than left to
 * rot into something a future reader mistakes for production data.
 *
 * Two things from the handoff are NOT reproduced here, and their absence is the
 * point:
 *
 *  · The risk bands' framing — "likely to be questioned", ranked "by how often
 *    each causes a refusal" — is outcome and likelihood language, which §3.1.3
 *    prohibits permanently and explicitly says survives authorisation. The risk
 *    screen is built; the copy states what is thin and what would strengthen
 *    it, and never how often it causes refusals. See ADR-0038.
 *  · "The route Verity recommends" on the financial screen. ADR-0022 point 1
 *    prohibits route recommendation outright. The routes are shown with their
 *    arithmetic; choosing between them is the user's.
 */

export interface SampleDoc {
  name: string;
  by: string;
  size: string;
  covers: string;
  added: string;
  check: string;
  checkState: "ok" | "attention";
}

export const SAMPLE = {
  application: { ref: "VS-2291", target: "2026-09-28", readiness: 43 },

  people: {
    applicant: { name: "Amara Koné", initials: "AK", place: "Dakar", role: "Applicant" },
    sponsor: { name: "Daniel Whitfield", initials: "DW", place: "Leeds", role: "Sponsor" },
  },

  documents: [
    {
      name: "Payslip — March 2026",
      by: "Daniel",
      size: "412 KB",
      covers: "Category A — salaried income",
      added: "18 August",
      check: "Matches the statement",
      checkState: "ok",
    },
    {
      name: "Bank statement — March 2026",
      by: "Daniel",
      size: "1.1 MB",
      covers: "Category A — salaried income",
      added: "18 August",
      check: "Matches the payslip",
      checkState: "ok",
    },
    {
      name: "Tenancy agreement",
      by: "Daniel",
      size: "2.4 MB",
      covers: "A legal right to occupy",
      added: "14 August",
      check: "Edge looks cropped",
      checkState: "attention",
    },
    {
      name: "Marriage certificate",
      by: "Amara",
      size: "780 KB",
      covers: "A valid marriage or civil partnership",
      added: "9 August",
      check: "Needs a certified translation",
      checkState: "attention",
    },
    {
      name: "Passport — biographic page",
      by: "Amara",
      size: "640 KB",
      covers: "Identity",
      added: "9 August",
      check: "Expires 4 Nov",
      checkState: "attention",
    },
  ] as SampleDoc[],

  translations: [
    { label: "Marriage certificate", state: "Awaiting a certified translation", met: false },
    { label: "Birth certificate", state: "Translated and certified", met: true },
    { label: "Bank statements (Senegal)", state: "Translated and certified", met: true },
    { label: "Tenancy agreement", state: "In English — no translation needed", met: true },
  ],

  scans: [
    { label: "Payslip — March 2026", note: "Clear, all four corners visible.", ok: true },
    { label: "Tenancy agreement", note: "Right edge cropped. Verity needs all four corners.", ok: false },
    { label: "Bank statement — March 2026", note: "Clear, and the account number is legible.", ok: true },
  ],

  /**
   * Bands renamed from the handoff. Its three were "Likely to be questioned",
   * "Worth strengthening" and "Settled and solid" — the first is a prediction
   * about a caseworker, which §3.1.3 does not permit. These describe the
   * evidence instead, which is the thing we can actually see.
   */
  risks: [
    {
      band: "thin",
      bandLabel: "Thin at the moment",
      title: "Cohabitation evidence is thin, and clustered",
      reasoning:
        "Four documents cover two years of living together, and two of them are from the same month. Appendix FM-SE asks for evidence spread across the whole period, and a caseworker reads a cluster as covering one moment rather than a continuous period.",
      owner: "Both of you",
      fixes: [
        "A joint account statement, even a dormant one, carries more weight than two bills in the same name.",
        "Aim for at least one document per quarter across the whole two years.",
        "Council tax or a tenancy in both names covers a long period in one document.",
      ],
    },
    {
      band: "unexplained",
      bandLabel: "Unexplained",
      title: "A £4,200 deposit inside the savings window",
      reasoning:
        "A single deposit of £4,200 lands eleven weeks into the six-month holding period. Cash savings have to be under your control for the whole period and from a lawful source, and a large deposit with nothing alongside it invites the question of where it came from.",
      owner: "Daniel",
      fixes: [
        "A letter from the person or body that sent it, naming the amount and the date.",
        "If it was a sale, the receipt or completion statement.",
        "If it was a gift, a signed letter from the giver confirming it is not a loan.",
      ],
    },
    {
      band: "timing",
      bandLabel: "A date to watch",
      title: "The English certificate expires six weeks after the target date",
      reasoning:
        "The certificate is valid to 9 November 2026. That is after the target submission date, so it works — but it leaves six weeks of margin, and any slip in the timetable puts it inside the window where it would need retaking.",
      owner: "Amara",
      fixes: [
        "Keep the submission date ahead of 9 November.",
        "If the date moves, book a retest before the old certificate lapses rather than after.",
      ],
    },
  ],

  ask: {
    suggested: [
      "Does my partner's overtime count towards the income requirement?",
      "What counts as proof we have lived together for two years?",
      "Do bank statements need to be stamped?",
      "Can I use savings and salary together?",
      "How recent do the payslips have to be?",
    ],
    thread: [
      {
        who: "Amara",
        question: "Do we need a letter from Daniel's landlord?",
        answer:
          "Yes, where the property is not owned or rented by the person applying. The rules ask for evidence of a legal right to occupy the accommodation, and where someone else owns or rents it, that is normally a letter from the owner or landlord confirming permission for you both to live there. Daniel's tenancy is in his name only, so the letter would come from his landlord. It has to be dated within 28 days of the date of application, and it can be reissued if it lapses.",
        drawnFrom: [
          "Appendix FM, E-ECP.3.4 — adequate accommodation",
          "Your answer: the property is rented, not owned",
          "Your document: tenancy agreement, uploaded 14 August",
        ],
      },
      {
        who: "Daniel",
        question: "Is the £4,200 deposit going to be a problem?",
        answer:
          "It is not automatically a problem, but it does need explaining. Cash savings must be under your control for the whole six-month period and come from a lawful source. A single large deposit part-way through the window is the kind of thing a caseworker asks about, and the answer is usually a short letter from whoever sent it. What matters is that the explanation exists in the bundle rather than being asked for later.",
        drawnFrom: [
          "Appendix FM-SE, paragraph 11A — cash savings",
          "Your document: savings account statement, 12 March 2026",
        ],
      },
    ],
  },

  together: {
    applicant: [
      { title: "Book the TB test in Dakar", note: "Clinics take about a week to return the certificate.", done: false },
      { title: "Add two address documents", note: "Anything from 2025 that shows you both at the same address.", done: false },
      { title: "Upload the English certificate", note: "The one issued in February.", done: true },
      { title: "Passport biographic page", note: "Photographed flat, all four corners.", done: true },
    ],
    sponsor: [
      { title: "Chase the bank letter", note: "Requested 12 August. Two weeks is normal.", done: false },
      { title: "Ask the landlord for permission in writing", note: "Needs to be dated within 28 days of applying.", done: false },
      { title: "Upload six months of payslips", note: "March to August.", done: true },
      { title: "Confirm the employment start date", note: "1 January 2024.", done: true },
    ],
    waiting: [
      { from: "Amara", to: "Daniel", ask: "Can you confirm which address you were at in early 2025?", age: "2 days", action: "Answer" },
      { from: "Daniel", to: "Amara", ask: "Is the marriage certificate translation booked?", age: "4 days", action: "Reply" },
      { from: "Verity", to: "Both", ask: "Agree the target submission date so the evidence windows can be worked back from it.", age: "6 days", action: "Agree" },
    ],
  },

  submission: {
    steps: [
      { title: "Finish the last four form answers", note: "About ten minutes.", state: "done" },
      { title: "Read the bundle together", note: "Both of you, before anything is paid.", state: "done" },
      { title: "Pay the visa fee and the health surcharge", note: "£1,938 and £1,035. Paid to the Home Office, not to Verity.", state: "next" },
      { title: "Book biometrics in Dakar", note: "The appointment slot is generated by the form.", state: "locked" },
      { title: "Print the bundle in Verity's order", note: "The order the caseworker expects to read it in.", state: "locked" },
      { title: "Hand in the documents", note: "At the appointment.", state: "locked" },
    ],
    fees: [
      { label: "Visa fee", amount: "£1,938" },
      { label: "Immigration health surcharge", amount: "£1,035" },
      { label: "Biometrics", amount: "£0" },
      { label: "Translations", amount: "£168" },
    ],
    total: "£3,141",
  },

  after: [
    { when: "Day 0", title: "Submitted", note: "The application is with the Home Office and the documents are handed in.", quiet: false },
    { when: "Week 1–2", title: "Acknowledgement", note: "Confirmation that the application has been received. This is administrative, not a decision.", quiet: false },
    { when: "Week 3–8", title: "Silence", note: "Nothing happens, visibly. This is the normal state of a straightforward application and it is not a signal of any kind.", quiet: true },
    { when: "Week 8–12", title: "Typical decision window", note: "Most straightforward partner applications from this country are decided in this period.", quiet: false },
    { when: "Beyond 12 weeks", title: "Longer than typical", note: "Still within the range. Chasing rarely helps before this point; after it, there is a process for asking.", quiet: false },
  ],

  /**
   * The assigned adviser. Everything here is illustrative — including the
   * registration number, which is formatted like a real IAA one and is not.
   *
   * This card makes a REGULATORY CLAIM: that a named, registered person is
   * advising on this case. It must be true before any real user sees it, and
   * until then it renders under the example marker like everything else. The
   * pooled-adviser model it implies is §13.2, still open.
   */
  adviser: {
    name: "Ruth Nabakooza",
    initials: "RN",
    level: "IAA Level 2 · Immigration Advice Authority",
    registration: "F0000000",
    assigned: "12 August 2026",
    note: "Reviews your evidence before you submit, and answers the questions the rules cannot.",
  },

  activity: [
    { who: "Daniel", kind: "sponsor", what: "Added six months of payslips", when: "2 hours ago" },
    { who: "Verity", kind: "system", what: "Income now reads £2,410 a month against £2,416 needed", when: "2 hours ago" },
    { who: "Amara", kind: "applicant", what: "Uploaded the English certificate", when: "Yesterday" },
    { who: "Ruth", kind: "adviser", what: "Looked at the cohabitation evidence", when: "3 days ago" },
    { who: "Verity", kind: "system", what: "Marked the tenancy scan as cropped", when: "4 days ago" },
    { who: "Amara", kind: "applicant", what: "Answered the accommodation questions", when: "5 days ago" },
  ],

  account: {
    plan: { price: "£89", paid: "12 June 2026" },
    people: [
      { name: "Amara Koné", initials: "AK", access: "Full access", seen: "Online now", removable: false },
      { name: "Daniel Whitfield", initials: "DW", access: "Full access", seen: "12 minutes ago", removable: false },
      { name: "Ruth Nabakooza", initials: "RN", access: "Read-only · adviser", seen: "3 days ago", removable: true },
    ],
    notifications: [
      { key: "expiring", label: "Dates that are about to expire", note: "A certificate or a document window running out.", on: true },
      { key: "activity", label: "When the other person does something", note: "Uploads, answers and completed tasks.", on: true },
      { key: "rules", label: "When the rules change", note: "If a figure or a requirement that affects your case moves.", on: true },
      { key: "digest", label: "A weekly summary on Sunday evening", note: "Where things stand and what is next.", on: false },
    ],
  },
} as const;
